import { Component, HostListener, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { CommonService } from './services/common.service';
import { HttpClient } from '@angular/common/http';
import { AppVersionService } from './services/app-version.service';
import mapboxgl from 'mapbox-gl';
import { environment } from 'src/environments/environment';
import { PageViewTrackerService } from './services/page-view-tracker.service';
import { FeatureFlagService } from './services/feature-flag.service';
import { OrganizationSettingsService } from './services/organization-settings.service';
import { MaintenanceAlertService } from './services/maintenance-alert.service';
import { GlobalSettingsService } from './services/global-settings.service';
import { finalize, tap, timeout } from 'rxjs/operators';
import { ErpAccessService } from './services/erp-access.service';
// Add to main.ts or app.component.ts
declare global {
  interface Window {
    viewVehicle: (id: number) => void;
  }
}

// Implement the function
window.viewVehicle = (id: number) => {
  // This will need to be implemented based on your routing
  console.log('View vehicle:', id);
};
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  isLoading = false;
  organizationLoadState: 'loading' | 'ready' | 'error' = 'loading';
  private organizationRequestPending = false;
  private organizationReturnUrl = '/login';
  private errorNavigation: Promise<boolean> | null = null;
  title = 'sms';
  isCollapsed = false;
  router!: string;
  private screenshotAttempts: number = 0;

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
  }

  constructor(
    private readonly route: Router,
    private readonly commonService: CommonService,
    private readonly pageViewTracker: PageViewTrackerService,
    private readonly http: HttpClient,
    private readonly featureFlagService: FeatureFlagService,
    private readonly organizationSettings: OrganizationSettingsService,
    private readonly maintenanceAlertService: MaintenanceAlertService,
    private readonly globalSettings: GlobalSettingsService,
    private readonly appVersionService: AppVersionService,
    private readonly location: Location,
    private readonly erpAccessService: ErpAccessService
  ) {
    this.route.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.router = e.url;
      }
    });
  }

  ngOnInit() {
    const currentUrl = this.location.path() || '/login';
    const requestedUrl = this.isServerErrorUrl(currentUrl)
      ? this.route.parseUrl(currentUrl).queryParams['returnUrl']
      : currentUrl;
    this.organizationReturnUrl = this.safeReturnUrl(requestedUrl);
    mapboxgl.accessToken = environment.mapboxToken;
    this.route.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.router = e.url;
        if (this.organizationLoadState === 'ready') {
          this.maintenanceAlertService.tryLoad();
          this.appVersionService.checkOnNavigation();
        }
      }
    });
    this.loadNoraPulseFromGds();
  }

  retryOrganizationLoad(): void {
    this.loadNoraPulseFromGds();
  }

  private isServerErrorUrl(url: string): boolean {
    return url.split(/[?#]/)[0] === '/server-error';
  }

  private safeReturnUrl(url: unknown): string {
    return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') &&
      !url.includes('\\') && !this.isServerErrorUrl(url) ? url : '/login';
  }

  private showOrganizationError(): void {
    this.organizationLoadState = 'error';
    this.errorNavigation = this.route.navigate(['/server-error'], {
      replaceUrl: true,
      queryParams: { returnUrl: this.organizationReturnUrl },
    }).catch(() => false);
  }

  private async finishOrganizationLoad(): Promise<void> {
    // Keep routed content blocked until recovery navigation completes.
    if (this.errorNavigation || this.isServerErrorUrl(this.location.path())) {
      try {
        if (this.errorNavigation) await this.errorNavigation;
        if (!await this.route.navigateByUrl(this.organizationReturnUrl, { replaceUrl: true })) {
          this.showOrganizationError();
          return;
        }
      } catch {
        this.showOrganizationError();
        return;
      }
    }
    this.errorNavigation = null;
    this.organizationLoadState = 'ready';
    this.pageViewTracker.start();
    this.appVersionService.init();
  }

  /**
   * Fetch org-level feature flags from GDS on every app launch and persist
   * them so the sidebar can filter menu items without waiting for login.
   */
  private loadNoraPulseFromGds(): void {
    if (this.organizationRequestPending || this.organizationLoadState === 'ready') return;
    this.organizationRequestPending = true;
    this.organizationLoadState = 'loading';
    const url = `${environment.apiUrl}Gds/organization-by-base-api?baseApi=${encodeURIComponent(environment.baseUrl)}`;
    this.http.get<any>(url).pipe(
      timeout(15000),
      tap(res => {
        const data = res?.data;
        const flags = data?.noraPulse;
        // Never unlock the app with missing configuration or stale cached menu flags.
        if (res?.success === false || !data || typeof data !== 'object' || Array.isArray(data) ||
            !flags || typeof flags !== 'object' || Array.isArray(flags)) {
          throw new Error('Organization configuration is unavailable.');
        }
        this.organizationSettings.setAdmissionNumberLabel(res?.data?.admissionNumber);
        this.organizationSettings.setFaceAuthDeviceSettings(res?.data?.faceAuthDeviceSettings);
        // Single source of truth for the org's country — loaded once here, never per-page.
        this.globalSettings.setCountry(res?.data?.country);
        const noraPulse = res?.data?.noraPulse;
        if (noraPulse) {
          this.featureFlagService.setFlags(noraPulse);
        }
        this.erpAccessService.setScope(res?.data?.erpAccessScope);
        const orgCode = res?.data?.orgCode;
        if (orgCode) {
          this.maintenanceAlertService.setOrgCode(orgCode);
        }
      }),
      finalize(() => { this.organizationRequestPending = false; }),
    ).subscribe({
      next: () => {
        void this.finishOrganizationLoad();
      },
      error: () => { this.showOrganizationError(); }
    });
  }

  // ===== SCREENSHOT PROTECTION METHODS =====

  /**
   * Block keyboard shortcuts for screenshots and printing
   */
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Block Print Screen
    if (event.key === 'PrintScreen') {
      this.logScreenshotAttempt('Print Screen key detected');
      event.preventDefault();
      this.flashWarning();
    }

    // Block Ctrl+P (Print)
    if (event.ctrlKey && event.key === 'p') {
      this.logScreenshotAttempt('Ctrl+P (Print) detected');
      event.preventDefault();
      this.flashWarning();
    }

    // Block Ctrl+Shift+P (Print in some browsers)
    if (event.ctrlKey && event.shiftKey && event.key === 'P') {
      this.logScreenshotAttempt('Ctrl+Shift+P detected');
      event.preventDefault();
      this.flashWarning();
    }

    // Block Windows + Print Screen
    if (event.metaKey && event.key === 'PrintScreen') {
      this.logScreenshotAttempt('Win+PrintScreen detected');
      event.preventDefault();
      this.flashWarning();
    }

    // Block F12 Developer Tools
    if (event.key === 'F12') {
      this.logScreenshotAttempt('F12 (DevTools) detected');
      event.preventDefault();
      this.flashWarning();
    }

    // Block Ctrl+Shift+I (Developer Tools)
    if (event.ctrlKey && event.shiftKey && event.key === 'I') {
      this.logScreenshotAttempt('Ctrl+Shift+I (DevTools) detected');
      event.preventDefault();
      this.flashWarning();
    }
  }

  /**
   * Block right-click context menu
   */
  // @HostListener('document:contextmenu', ['$event'])
  // onRightClick(event: MouseEvent) {
  //   event.preventDefault();
  //   return false;
  // }

  /**
   * Log screenshot attempts (can be sent to backend)
   */
  private logScreenshotAttempt(reason: string) {
    this.screenshotAttempts++;
    const userData = localStorage.getItem('user');
    let username = 'Unknown';

    if (userData) {
      try {
        const user = JSON.parse(userData);
        username = user.username || user.email || user.name || 'Unknown';
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    const logEntry = {
      timestamp: new Date().toISOString(),
      user: username,
      reason: reason,
      attemptNumber: this.screenshotAttempts,
      url: this.router
    };

    console.warn('⚠️ Screenshot Protection:', logEntry);

    // Note: Implement backend logging endpoint for production use
    // Example: this.http.post('/api/security/screenshot-attempt', logEntry).subscribe();
  }

  /**
   * Show warning message using snackbar
   */
  private flashWarning() {
    this.commonService.openSnackBarError('⚠️ Screenshots are not allowed');
  }

  // ===== END SCREENSHOT PROTECTION =====

  currentLinkName = "";

  navlist = [
    {name:"Dashboard", linkto:'/dashboard', icon:""},
    {name:"User Management", linkto:'/user-management', icon:""},
    {name:"Course Management", linkto:'/course-management', icon:""},
    {name:"Timetable Management", linkto:'/time-table-management', icon:""},
    {name:"Attendance Tracking and Reports", linkto:'/attendance-tracking-reports', icon:""},
    {name:"Admission management", linkto:'/admission-management', icon:""},
    {name:"Classes", linkto:'/classes', icon:""},
    {name:"Transportation tracking management", linkto:'/transportation-tracking-management', icon:""},
    {name:"payment", linkto:'/payment', icon:""},
    {name:"online resources", linkto:'/online-resources', icon:""},
    {name:"Gradebook", linkto:'/gradebook', icon:""},
    {name:"Settings", linkto:'/settings', icon:""},
  ];

}
